import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type SignInResult } from '../lib/api'
import { Avatar, ErrorText } from '../components/ui'

export function SignIn() {
  const queryClient = useQueryClient()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [choices, setChoices] = useState<SignInResult['households']>()

  const signIn = useMutation({
    mutationFn: () => api.signIn(identifier.trim(), password),
    onSuccess: async (result) => {
      if (result.needsChoice) {
        setChoices(result.households)
        return
      }
      await queryClient.invalidateQueries()
    },
  })

  const choose = useMutation({
    mutationFn: (memberId: string) => api.chooseHousehold(identifier.trim(), password, memberId),
    onSuccess: async () => {
      await queryClient.invalidateQueries()
    },
  })

  if (choices && choices.length > 0) {
    return (
      <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
        <h1 className="title">Which household?</h1>
        <p className="subtitle">Your account is attached to more than one.</p>
        <div className="list">
          {choices.map((choice) => (
            <button
              key={choice.memberId}
              type="button"
              className="check"
              disabled={choose.isPending}
              onClick={() => choose.mutate(choice.memberId)}
            >
              <Avatar emoji={choice.emoji} />
              <span className="check-body">
                <span className="check-title">{choice.householdName}</span>
                <span className="check-detail">as {choice.name}</span>
              </span>
            </button>
          ))}
        </div>
        <ErrorText error={choose.error} />
      </div>
    )
  }

  return (
    <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
      <h1 className="title" style={{ fontSize: '1.7rem' }}>
        Sign in
      </h1>
      <p className="subtitle">Parents use their email. Kids use the username a parent set up for them.</p>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          signIn.mutate()
        }}
      >
        <div className="field">
          <label className="label" htmlFor="signin-identifier">
            Email or username
          </label>
          <input
            id="signin-identifier"
            className="input"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="signin-password">
            Password
          </label>
          <input
            id="signin-password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <ErrorText error={signIn.error} />

        <button
          type="submit"
          className="btn btn-primary btn-block btn-lg"
          disabled={!identifier.trim() || !password || signIn.isPending}
        >
          Sign in
        </button>
      </form>

      <div className="stack">
        <Link to="/forgot" className="btn btn-quiet btn-block">
          I've forgotten my password
        </Link>
        <Link to="/" className="btn btn-quiet btn-block">
          Back
        </Link>
      </div>
    </div>
  )
}
